import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { NzMessageService } from 'ng-zorro-antd/message';
import { isStaleChunkError, recoverFromStaleChunk } from 'src/utils/stale-chunk-recovery';

@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  // Injected lazily via Injector rather than the constructor: ErrorHandler can be asked
  // to handle errors thrown during Angular's own bootstrap, before DI has finished
  // wiring up the app's other providers.
  constructor(private injector: Injector) {
    super();
  }

  override handleError(error: unknown): void {
    if (!isStaleChunkError(error)) {
      this.surfaceOnScreen(error);
      super.handleError(error);
      return;
    }

    const updates = this.injector.get(SwUpdate);
    if (!updates.isEnabled) {
      // No service worker (e.g. localhost) means there's no way to confirm this is an
      // actual new deployment rather than a transient network failure — surface the
      // error normally instead of forcing a reload that likely won't fix anything.
      super.handleError(error);
      return;
    }

    updates
      .checkForUpdate()
      .then((newVersionAvailable) => {
        if (newVersionAvailable) {
          console.error('Stale chunk load caused by a new deployment — recovering by unregistering the service worker and reloading:', error);
          recoverFromStaleChunk();
        } else {
          console.error('Stale chunk load, but no new deployment was found — not auto-reloading:', error);
          super.handleError(error);
        }
      })
      // Couldn't even confirm whether a new version exists (e.g. the update check itself
      // is offline) — default to not reloading rather than guessing.
      .catch(() => super.handleError(error));
  }

  // Any uncaught error (a bad template binding, a null-reference in a component, etc.)
  // previously only reached the browser console via ErrorHandler's default handleError —
  // invisible to anyone not already watching devtools. Surfacing it as a toast too means a
  // silently-blank screen always comes with an on-screen reason instead of requiring devtools
  // to diagnose.
  private surfaceOnScreen(error: unknown): void {
    try {
      const message = this.injector.get(NzMessageService);
      const text = error instanceof Error ? error.message : String(error);
      message.create('error', `Unexpected error: ${text}`, { nzDuration: 15000 });
    } catch {
      // NzMessageService not available yet (e.g. error thrown during bootstrap) — the
      // console log from super.handleError() is still the fallback.
    }
  }
}
